import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TrafficDeviationSearchStore } from '@components/traffic-deviations/components/searches/stores/traffic-deviation-search.store';
import { TranslateModule } from '@ngx-translate/core';
import { LineSearchResult, LineSearchStop, LineSearchStopArea } from '../../interfaces/line-search-result';
import { TrafficDeviationSearchService } from '@components/traffic-deviations/components/searches/services/traffic-deviation-search.service';
import { BehaviorSubject, combineLatest, firstValueFrom, map, Observable, Subject, take, takeUntil } from 'rxjs';
import { EffectedRoutesOnStop, StopSearchResult } from '../../interfaces';
import { TrafficDeviationService } from '@components/traffic-deviations/services/traffic-deviation.service';
import { ActivatedRoute, Router } from '@angular/router';
import { SpinnerComponent } from '@modules/shared/components';
import { BladeHeaderComponent } from '@modules/blades/components/blade-header/blade-header.component';

@Component({
    selector: 'app-selected-line-stop-list',
    imports: [CommonModule, TranslateModule, SpinnerComponent, BladeHeaderComponent],
    templateUrl: './selected-line-stop-list.component.html',
    styleUrls: ['./selected-line-stop-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SelectedLineStopListComponent implements OnInit, OnDestroy {
  private readonly _router = inject(Router);
  private readonly _activeRoute = inject(ActivatedRoute);
  private readonly _searchStore = inject(TrafficDeviationSearchStore);
  private readonly _searchService = inject(TrafficDeviationSearchService);
  private readonly _trafficDeviationService = inject(TrafficDeviationService);
  
  protected selectedLine$: Observable<LineSearchResult | undefined>;
  protected readonly stopsPlaces$ = new BehaviorSubject<LineSearchStop[]>([]);

  private _showStopPlaces = false;

  private _isProcessingQueue = false;
  private readonly _destroy$ = new Subject<void>();
  private readonly _selectStopAreaQueue: Array<() => Promise<void>> = [];

  private _isDragging = false;
  private _dragTargetState: boolean | null = null;
  private _lastClickedId: string | null = null;
  private _dragPath: Array<{
      id: string;
      toggled: boolean;
      item: LineSearchStop | LineSearchStopArea;
      line: LineSearchResult;
  }> = [];
  private readonly _onMouseUp = (): void => {
      this._isDragging = false;
      this._dragTargetState = null;
      this._dragPath = [];
  };

  ngOnInit(): void {
    this._showStopPlaces = this._activeRoute.snapshot.url[1].path === 'stop-places';
    document.addEventListener("mouseup", this._onMouseUp);

    this._trafficDeviationService.deviationTypeOptions.valueChanges.pipe(
      takeUntil(this._destroy$)
    ).subscribe(() => {
      this._router.navigate(['', { outlets: { outlet1: null }}]);
    });

    this.selectedLine$ = combineLatest([
      this._searchService.choosenLines$,
      this._searchService.selectedLineInBlade$, 
      this._searchStore.getSelectedStopSearches()
    ]).pipe(
      map(([selectedLines, selectedLine, stopSearches]) => {
        let line: LineSearchResult;
        
        this._trafficDeviationService.deviationType$.pipe(take(1)).subscribe(
          (type) => {
            if (type.deviationInformation) {
              line = selectedLines?.find((l) => {
                return l.routeId === selectedLine?.routeId
                  && l.firstStopDestinationSign === selectedLine.firstStopDestinationSign;
              });
            }
            if (type.journeyPattern) line = selectedLine;
          }
        );
        
        if (!line) return;

        let stopPlaces: LineSearchStop[] = [];
        line.stopPlaces = [...line.stopPlaces];
        for (const stopArea of line.stopPlaces) {
          stopArea.stopPlaces = stopArea.stopPlaces.map((stop) => {
            const stopIndex = stopSearches.findIndex((s) => {
              return s.type === 'StopPlace' && s.placeId === stop.placeId;
            });
            if (stopIndex !== -1) {
              const searchStop = stopSearches[stopIndex];
              return {...stop, effectedRoutes: searchStop.effectedRoutes};
            } 
            return {...stop, effectedRoutes: stop.effectedRoutes?.map((er) => {
              return {...er};
            })};
          });

          let hasActiveEffectedRoute = false;
          for (const stop of stopArea.stopPlaces) {
            const route = stop.effectedRoutes?.find((er) => {
              return er.routeId === line.routeId 
                && er.firstStopDestinationSign === line.firstStopDestinationSign;
            });
            if (route?.active) {
              hasActiveEffectedRoute = true;
              break;
            }
          }
          stopArea.selected = hasActiveEffectedRoute;
          
          if (this._showStopPlaces) stopPlaces = stopPlaces.concat(stopArea.stopPlaces);
        }
          
        if (this._showStopPlaces) this.stopsPlaces$.next(stopPlaces);
        return line;
      })
    );

    this._searchService.selectedLineInBlade$.pipe(
      takeUntil(this._destroy$)
    ).subscribe((selectedLineInBlade) => {
      if (!selectedLineInBlade) this._router.navigate(['', { outlets: { outlet1: null }}]);
    });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
    this._searchService.selectedLineInBlade$.next(undefined);
    document.removeEventListener("mouseup", this._onMouseUp);
  }

  protected onStopPlaceMouseDown(
      event: MouseEvent,
      stopPlace: LineSearchStop,
      lineSearchResult: LineSearchResult,
      allStopPlaces: LineSearchStop[],
  ): void {
      event.preventDefault();
      if (event.shiftKey && this._lastClickedId) {
          const anchorIdx = allStopPlaces.findIndex(
              (s) => s.placeId === this._lastClickedId,
          );
          const targetIdx = allStopPlaces.findIndex(
              (s) => s.placeId === stopPlace.placeId,
          );
          if (anchorIdx !== -1 && targetIdx !== -1) {
              const [from, to] =
                  anchorIdx < targetIdx
                      ? [anchorIdx, targetIdx]
                      : [targetIdx, anchorIdx];
                      
              const targetState = !stopPlace.selected;
              for (let i = from; i <= to; i++) {
                  if (allStopPlaces[i].selected !== targetState) {
                      this.selectStopPlace(
                          allStopPlaces[i],
                          lineSearchResult,
                      );
                  }
              }
              this._lastClickedId = stopPlace.placeId;
              return;
          }
      }
      this._isDragging = true;
      this._dragTargetState = !stopPlace.selected;
      this._dragPath = [
          {
              id: stopPlace.placeId,
              toggled: true,
              item: stopPlace,
              line: lineSearchResult,
          },
      ];
      this._lastClickedId = stopPlace.placeId;
      this.selectStopPlace(stopPlace, lineSearchResult);
  }

  protected onStopPlaceMouseEnter(
      stopPlace: LineSearchStop,
      lineSearchResult: LineSearchResult,
  ): void {
      if (!this._isDragging) return;
      const idx = this._dragPath.findIndex((e) => e.id === stopPlace.placeId);
      if (idx === -1) {
          const toggled = stopPlace.selected !== this._dragTargetState;
          this._dragPath.push({
              id: stopPlace.placeId,
              toggled,
              item: stopPlace,
              line: lineSearchResult,
          });
          if (toggled) this.selectStopPlace(stopPlace, lineSearchResult);
      } else {
          const reverted = this._dragPath.splice(idx + 1);
          for (const entry of reverted) {
              if (entry.toggled)
                  this.selectStopPlace(
                      entry.item as LineSearchStop,
                      entry.line,
                  );
          }
      }
  }

  protected onStopAreaClick(
      stopArea: LineSearchStopArea,
      lineSearchResult: LineSearchResult,
  ): void {
      this.selectStopArea(stopArea, lineSearchResult);
  }

  protected async selectStopArea(
    stopArea: LineSearchStopArea, 
    lineSearchResult: LineSearchResult
  ): Promise<void> {
    this._selectStopAreaQueue.push(() => this._selectStopArea(stopArea, lineSearchResult));

    if (!this._isProcessingQueue) {
      this._isProcessingQueue = true;
      while (this._selectStopAreaQueue.length) {
        await this._selectStopAreaQueue.shift()();
      }
      this._isProcessingQueue = false;
    }    
  }

  protected selectStopPlace(selectedStopPlace: LineSearchStop, lineSearchResult: LineSearchResult): void {
    const selectedLines = [...this._searchService.choosenLines$.getValue()];
    
    for (let i = 0; i < selectedLines.length; i++) {
      const line = selectedLines[i];
      if (
        line.routeId === lineSearchResult.routeId
        && line.firstStopDestinationSign === lineSearchResult.firstStopDestinationSign
      ) {
        const newLine = {...line};
        selectedLines.splice(i, 1, newLine)
        for (let j = 0; j < newLine.stopPlaces.length; j++) {
          const stopArea = newLine.stopPlaces[j];
          const stopPlaceIndex = stopArea.stopPlaces.findIndex((stop) => {
            return stop.placeId === selectedStopPlace.placeId;
          });
          if (stopPlaceIndex !== -1) {
            const newStopAreas = [...newLine.stopPlaces];
            const newStopArea = {...stopArea};
            newStopAreas.splice(j, 1, newStopArea);
            newStopArea.stopPlaces = [...newStopArea.stopPlaces];
            
            const newStopPlace = { 
              ...newStopArea.stopPlaces[stopPlaceIndex], 
              effectedRoutes: [{
                active: true,
                firstStopDestinationSign: lineSearchResult.firstStopDestinationSign
              } as EffectedRoutesOnStop] 
            };
            newStopPlace.selected = !newStopPlace.selected;
            newStopArea.stopPlaces.splice(stopPlaceIndex, 1, newStopPlace);

            newLine.stopPlaces = newStopAreas;
            break;
          }
        }
        break;
      }
    }

    this._searchService.choosenLines$.next(selectedLines);
  }

  protected trackByStopPlaces(index: number, stop: LineSearchStopArea | LineSearchStop): string {
    const id = 'stopAreaId' in stop ? stop.stopAreaId : stop.placeId;
    return `${id}|${index}`;
  }

  private async _selectStopArea(
    stopArea: LineSearchStopArea, lineSearchResult: LineSearchResult
  ): Promise<void> {
    stopArea.selected = !stopArea.selected;
    let selectedStopSearches: StopSearchResult[] = [...await firstValueFrom(
      this._searchStore.getSelectedStopSearches()
    )];
    
    if (!stopArea.selected) {
      this._handleStopAreaDeselection(stopArea, selectedStopSearches, lineSearchResult);
      return;
    }

    selectedStopSearches = await this._handleStopAreaSelection(stopArea, lineSearchResult, selectedStopSearches);
    this._searchStore.updateSearchState({ selectedStopSearches });
  }

  private async _handleStopAreaSelection(
    stopArea: LineSearchStopArea, 
    lineSearchResult: LineSearchResult, 
    selectedStopSearches: StopSearchResult[]
  ): Promise<StopSearchResult[]> {
    stopArea.effectedRoutesLoader = true;
    await this._loadEffectedRoutes(stopArea, lineSearchResult);
    
    selectedStopSearches = this._updateStopPlaces(stopArea, lineSearchResult, selectedStopSearches);
    stopArea.effectedRoutesLoader = false;

    selectedStopSearches = this._updateStopArea(stopArea, selectedStopSearches);
    return structuredClone(selectedStopSearches);
  }

  private async _loadEffectedRoutes(stopArea: LineSearchStopArea, lineSearchResult: LineSearchResult): Promise<void> {
    await Promise.all(stopArea.stopPlaces.map(async (stop) => {
      stop.effectedRoutes = await this._searchService.getRoutesForStop(
        stop, false, lineSearchResult);
    }));
  }

  private _updateStopPlaces(
    stopArea: LineSearchStopArea, 
    lineSearchResult: LineSearchResult, 
    selectedStopSearches: StopSearchResult[]
  ): StopSearchResult[] {
    for (const stop of stopArea.stopPlaces) {
      if (!stop.effectedRoutes?.length) continue;

      const stopPlaceIndex = selectedStopSearches.findIndex((s) => s.placeId === stop.placeId);
      if (stopPlaceIndex === -1) {
        selectedStopSearches.push(this._createNewStopPlace(stop));
      } else {
        this._updateExistingStopPlace(selectedStopSearches[stopPlaceIndex], stop, lineSearchResult);
      }
    }
    return selectedStopSearches;
  }

  private _createNewStopPlace(stop: LineSearchStop): StopSearchResult {
    return {
      hastusRegion: stop.hastusRegion,
      description: stop.placeDescription,
      platformCode: stop.platformCode,
      placeId: stop.placeId,
      parentId: stop.parentId,
      placeLatitude: stop.placeLatitude,
      placeLongitude: stop.placeLongitude,
      active: true,
      replaceName: '',
      replacePlaceId: '',
      searchType: 'StopSearch',
      type: 'StopPlace',
      selected: false,
      effectedRoutes: stop.effectedRoutes,
      stopSearchResults: []
    };
  }

  private _updateStopArea(
    stopArea: LineSearchStopArea, selectedStopSearches: StopSearchResult[]
  ): StopSearchResult[] {
    const stopAreaIndex = selectedStopSearches.findIndex(x => 
      x.type === 'StopArea' && x.placeId === stopArea.stopAreaId
    );
    const stopPlaceOfAreaExists = selectedStopSearches.some(x => 
      x.type === 'StopPlace' && x.parentId === stopArea.stopAreaId
    );

    if (stopAreaIndex === -1 && stopPlaceOfAreaExists) {
      selectedStopSearches.push({
        type: 'StopArea',
        searchType: 'StopSearch',
        description: stopArea.stopAreaDescription,
        placeId: stopArea.stopAreaId,
        hastusRegion: stopArea.hastusRegion,
        stopPlaces: []
      });
    } else if (stopAreaIndex === -1) {
      stopArea.selected = !stopArea.selected;
    }

    return selectedStopSearches;
  }

  private _updateExistingStopPlace(
    stopPlace: StopSearchResult, 
    stop: LineSearchStop, 
    lineSearchResult: LineSearchResult
  ): void {
    for (const route of stop.effectedRoutes) {
      const effectedRoute = stopPlace.effectedRoutes.find(er => 
        er.routeId === route.routeId && 
        er.firstStopDestinationSign === route.firstStopDestinationSign
      );

      if (!effectedRoute) {
        stopPlace.effectedRoutes.push(route);
        continue;
      }

      if (!effectedRoute.active) {
        effectedRoute.active = lineSearchResult.routeId === route.routeId && 
          lineSearchResult.firstStopDestinationSign === route.firstStopDestinationSign;
      }
    }
  }

  private _handleStopAreaDeselection(
    stopArea: LineSearchStopArea, 
    selectedStopSearches: StopSearchResult[], 
    lineSearchResult: LineSearchResult
  ): void {
    for (const stop of stopArea.stopPlaces) {
      const stopPlaceIndex = selectedStopSearches.findIndex((s) => s.placeId === stop.placeId);
      if (stopPlaceIndex !== -1) {
        const stopPlace = {...selectedStopSearches[stopPlaceIndex]};
        const effectedRouteIndex = stopPlace.effectedRoutes.findIndex((er) => {
          return er.routeId === lineSearchResult.routeId 
              && er.firstStopDestinationSign === lineSearchResult.firstStopDestinationSign;
        });
        if (effectedRouteIndex !== -1) {
          const route = {...stopPlace.effectedRoutes[effectedRouteIndex]};
          route.active = false;
          stopPlace.effectedRoutes.splice(effectedRouteIndex, 1, route);
          selectedStopSearches.splice(stopPlaceIndex, 1, stopPlace);
        }
        
        let hasActiveEffectedRoute = false;
        for (const stop of stopArea.stopPlaces) {
          hasActiveEffectedRoute = stop.effectedRoutes?.some((er) => er.active);
          if (hasActiveEffectedRoute) break;
        }
        if (!hasActiveEffectedRoute) {
          selectedStopSearches = selectedStopSearches.filter((stop) => {
            if (
              (stop.type === 'StopArea' && stop.placeId === stopArea.stopAreaId)
              || (stop.type === 'StopPlace' 
                && stopArea.stopPlaces.some((s) => {
                  return s.placeId === stop.placeId;
                }))
            ) {
                return false;
              }
            return true;
          });
        }
      }
    }

    this._searchStore.updateSearchState({ selectedStopSearches });
  }
}
